import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import { InfoService } from '../../../services/site/InfoService';
import './LegalStyles.scss';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
}> = ({ children, className = '' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};


// ============================================
// TERMS & CONDITIONS COMPONENT
// ============================================
export const TermsAndConditions: React.FC = () => {
  return (
    <div className="sds_legal-wrapper">
      {/* SEO Meta */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions Terms and Conditions</h1>
        <meta name="description" content="HDT Solutions terms and conditions. Read our service agreement and user obligations." />
      </div>

      <div className="container">
        <AnimatedSection className="sds_legal-header section">
          <div className="has-text-centered">
            <motion.div 
              className="sds_legal-icon"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              📋
            </motion.div>
            <h1 className="title is-1 sds_legal-title">Terms & Conditions</h1>
            <p className="subtitle is-5 sds_legal-subtitle">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="sds_legal-intro">
              Please read these Terms and Conditions carefully before using HDT Solutions services.
            </p>
          </div>
        </AnimatedSection>

        <motion.div variants={staggerContainer} initial="hidden" animate="visible">
          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">1. Acceptance of Terms</h2>
            <div className="content">
              <p>By accessing or using HDT Solutions services, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.</p>
              <p className="mt-4">These terms apply to:</p>
              <ul>
                <li>All users of our website and applications</li>
                <li>Customers purchasing or subscribing to our services</li>
                <li>Trial users and free tier users</li>
                <li>API users and integration partners</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">2. Service Description</h2>
            <div className="content">
              <p>HDT Solutions provides the following services:</p>
              <ul>
                <li><strong>Digital Signature Solutions:</strong> Electronic document signing and verification</li>
                <li><strong>AI Data Extraction:</strong> Automated document data extraction and processing</li>
                <li><strong>Chatbot Solutions:</strong> AI-powered conversational interfaces</li>
                <li><strong>Template Automation:</strong> Document generation and management</li>
                <li><strong>Content Security Policy:</strong> Website security implementation</li>
              </ul>
              <div className="notification is-info is-light mt-4">
                <p><strong>Service Availability:</strong> We strive for 99.9% uptime but do not guarantee uninterrupted service. Scheduled maintenance will be announced in advance.</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">3. User Obligations</h2>
            <div className="content">
              <h3 className="title is-5">3.1 Account Responsibilities</h3>
              <p>You are responsible for:</p>
              <ul>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Ensuring your account information is accurate and up-to-date</li>
                <li>Notifying us immediately of any unauthorized access</li>
              </ul>

              <h3 className="title is-5 mt-5">3.2 Prohibited Activities</h3>
              <p>You agree not to:</p>
              <ul>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Upload malicious code or viruses</li>
                <li>Attempt to gain unauthorized access to systems</li>
                <li>Use services for illegal or fraudulent purposes</li>
                <li>Reverse engineer or decompile our software</li>
                <li>Resell or redistribute our services without authorization</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">4. Payment Terms</h2>
            <div className="content">
              <h3 className="title is-5">4.1 Fees and Billing</h3>
              <ul>
                <li>All fees are in Indian Rupees (INR) unless otherwise specified</li>
                <li>Subscription fees are billed in advance on a monthly or annual basis</li>
                <li>Usage-based fees are calculated and billed monthly</li>
                <li>Prices are subject to change with 30 days notice</li>
              </ul>

              <h3 className="title is-5 mt-5">4.2 Refund Policy</h3>
              <ul>
                <li>30-day money-back guarantee for new subscriptions</li>
                <li>Refunds processed within 7-10 business days</li>
                <li>No refunds for monthly subscriptions after 30 days</li>
                <li>Annual subscriptions: pro-rated refunds after 30-day period</li>
              </ul>

              <h3 className="title is-5 mt-5">4.3 Late Payments</h3>
              <p>If payment is not received:</p>
              <ul>
                <li>Services may be suspended after 7 days of non-payment</li>
                <li>Late fee of 1.5% per month may be applied</li>
                <li>Account may be terminated after 30 days</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">5. Intellectual Property Rights</h2>
            <div className="content">
              <h3 className="title is-5">5.1 Our Rights</h3>
              <p>All content, features, and functionality of our services are owned by HDT Solutions and protected by copyright, trademark, and other intellectual property laws.</p>

              <h3 className="title is-5 mt-5">5.2 Your Rights</h3>
              <p>You retain all rights to content you upload or create using our services. By using our services, you grant us a limited license to process and store your content for service delivery.</p>

              <h3 className="title is-5 mt-5">5.3 Feedback</h3>
              <p>Any feedback, suggestions, or ideas you provide may be used by us without obligation or compensation to you.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">6. Data Protection and Privacy</h2>
            <div className="content">
              <p>Your use of our services is also governed by our Privacy Policy. We comply with:</p>
              <ul>
                <li>Information Technology Act, 2000 (India)</li>
                <li>General Data Protection Regulation (GDPR)</li>
                <li>California Consumer Privacy Act (CCPA)</li>
                <li>Industry-specific regulations (HIPAA, PCI DSS, etc.)</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">7. Limitation of Liability</h2>
            <div className="content">
              <p>To the maximum extent permitted by law:</p>
              <ul>
                <li>HDT Solutions shall not be liable for indirect, incidental, or consequential damages</li>
                <li>Total liability is limited to fees paid in the 12 months prior to the claim</li>
                <li>We are not responsible for third-party services or content</li>
                <li>We do not guarantee specific results or outcomes</li>
              </ul>
              <div className="notification is-warning is-light mt-4">
                <p><strong>Disclaimer:</strong> Services are provided "as is" without warranties of any kind, express or implied.</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">8. Termination</h2>
            <div className="content">
              <h3 className="title is-5">8.1 By You</h3>
              <p>You may terminate your account at any time through account settings or by contacting support.</p>

              <h3 className="title is-5 mt-5">8.2 By Us</h3>
              <p>We may suspend or terminate your account if:</p>
              <ul>
                <li>You violate these Terms and Conditions</li>
                <li>Your account is inactive for 12 months</li>
                <li>Payment is not received after 30 days</li>
                <li>We discontinue the service (with 90 days notice)</li>
              </ul>

              <h3 className="title is-5 mt-5">8.3 Effect of Termination</h3>
              <ul>
                <li>Access to services will be discontinued</li>
                <li>Data will be retained for 30 days for recovery</li>
                <li>After 30 days, data may be permanently deleted</li>
                <li>Outstanding fees remain due and payable</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">9. Governing Law and Dispute Resolution</h2>
            <div className="content">
              <p>These Terms are governed by the laws of India. Any disputes will be resolved through:</p>
              <ol>
                <li><strong>Negotiation:</strong> Good faith discussions for 30 days</li>
                <li><strong>Mediation:</strong> Binding mediation if negotiation fails</li>
                <li><strong>Arbitration:</strong> Arbitration in Hyderabad, India</li>
                <li><strong>Legal Action:</strong> Courts of Hyderabad have exclusive jurisdiction</li>
              </ol>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">10. Changes to Terms</h2>
            <div className="content">
              <p>We may modify these Terms at any time. Changes will be effective:</p>
              <ul>
                <li>Immediately upon posting for non-material changes</li>
                <li>30 days after notice for material changes</li>
              </ul>
              <p className="mt-4">Continued use after changes constitutes acceptance. If you don't agree, you must stop using our services.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-contact">
            <h2 className="title is-3 sds_legal-section-title">11. Contact Information</h2>
            <div className="content">
              <p>For questions about these Terms and Conditions:</p>
              <div className="box sds_legal-contact-box">
                <p><strong>HDT Solutions </strong></p>
                <p>📧 Email: <a href="mailto:sds@hdtsolution.com">legal@hdtsolution.com</a></p>
                <p>📱 Phone: <a href="tel:+91 9553896536">{InfoService?.mobile_no_1}</a></p>
                <p>🌐 Website: <a href="https://hdtsecure.com/">hdtsecure.com</a></p>
              </div>
            </div>
          </AnimatedSection>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
